/**
 * Long plank grain. Each plate is a generated flat-sawn board, tinted to the
 * glen brown. A panel takes one plate, flips it, and either crops a window or
 * stretches it along the grain. It never repeats.
 */

export const GRAIN_PLATES = [
  { key: 'grain-plank-0', path: 'assets/ui/grain/plank-0.webp' },
  { key: 'grain-plank-1', path: 'assets/ui/grain/plank-1.webp' },
  { key: 'grain-plank-2', path: 'assets/ui/grain/plank-2.webp' },
  { key: 'grain-plank-3', path: 'assets/ui/grain/plank-3.webp' },
] as const;

export const GRAIN_PLATE_COUNT = GRAIN_PLATES.length;
/** Along the grain. */
export const GRAIN_LEN = 2048;
/** Across the grain. */
export const GRAIN_CROSS = 1152;

export interface GrainWindow {
  plate: number;
  flipAlong: boolean;
  flipCross: boolean;
  /** Grain runs down the panel instead of across it. */
  vertical: boolean;
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  /** The panel is longer than the plate, so only the grain axis stretches. */
  stretchGrain: boolean;
  bucket: number;
}

export interface GrainPlate {
  canvas: HTMLCanvasElement;
  mean: [number, number, number];
}

/** Nearby planks, including a shadow offset by a few pixels, share a window. */
export function grainSeed(x: number, y: number, w: number, h: number): number {
  const qx = Math.floor((x + 48) / 96);
  const qy = Math.floor((y + 48) / 96);
  let n = Math.imul(qx, 374761393) ^ Math.imul(qy, 668265263);
  n ^= Math.imul(Math.round(w), 1274126177) ^ Math.imul(Math.round(h), 2246822519);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return (n ^ (n >>> 16)) >>> 0;
}

let salt = 0;

/** Turn banners have no place of their own, so each one takes the next window. */
export function nextGrainSalt(): number {
  salt = (salt + 1) % 32;
  return salt + 1;
}

/**
 * How a face samples a plate. Width and height are the face in pixels.
 * The source rect stays inside the plate, so the draw cannot tile.
 */
export function grainSample(seed: number, faceW: number, faceH: number): GrainWindow {
  const plate = seed % GRAIN_PLATE_COUNT;
  const flipAlong = ((seed >>> 2) & 1) === 1;
  const flipCross = ((seed >>> 3) & 1) === 1;
  const bucket = (seed >>> 4) & 7;
  const vertical = faceH > faceW;
  const grainLen = Math.max(1, vertical ? faceH : faceW);
  const grainCross = Math.max(1, vertical ? faceW : faceH);
  const crossScale = grainCross / GRAIN_CROSS;
  const naturalLen = GRAIN_LEN * crossScale;
  if (naturalLen >= grainLen) {
    const sw = grainLen / crossScale;
    const maxOff = Math.max(0, GRAIN_LEN - sw);
    return {
      plate,
      flipAlong,
      flipCross,
      vertical,
      sx: maxOff * (bucket / 7),
      sy: 0,
      sw,
      sh: GRAIN_CROSS,
      stretchGrain: false,
      bucket,
    };
  }
  return {
    plate,
    flipAlong,
    flipCross,
    vertical,
    sx: 0,
    sy: 0,
    sw: GRAIN_LEN,
    sh: GRAIN_CROSS,
    stretchGrain: true,
    bucket,
  };
}

/**
 * How far the grain reaches into a nine-slice cap. The cap includes flat face
 * inside the carve, so the lip is only the carved edge, scaled with the cap.
 */
export function frameInset(cap: number, slice: number, lip: number): number {
  if (slice <= 0 || cap <= 0) return 0;
  return Math.max(0, Math.min(cap, Math.round((cap * lip) / slice)));
}

export function grainVariant(sample: GrainWindow): string {
  const flip = `${sample.flipAlong ? 1 : 0}${sample.flipCross ? 1 : 0}`;
  if (sample.stretchGrain) return `s${sample.plate}${flip}`;
  return `c${sample.plate}${flip}${sample.bucket}`;
}

interface TextureHost {
  textures: {
    exists(key: string): boolean;
    get(key: string): { getSourceImage(): unknown };
  };
}

function plateMean(canvas: HTMLCanvasElement): [number, number, number] {
  const ctx = canvas.getContext('2d');
  if (!ctx) return [141, 88, 48];
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let i = 0; i < data.length; i += 16) {
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    n += 1;
  }
  return [r / n, g / n, b / n];
}

let plates: GrainPlate[] | null = null;

/** Copy the loaded plank photographs. Returns null until boot has them. */
export function ensureGrainPlates(scene: TextureHost): GrainPlate[] | null {
  if (plates) return plates;
  if (typeof document === 'undefined') return null;
  if (!GRAIN_PLATES.every((plate) => scene.textures.exists(plate.key))) return null;
  plates = GRAIN_PLATES.map((plate) => {
    const source = scene.textures.get(plate.key).getSourceImage();
    const canvas = document.createElement('canvas');
    canvas.width = GRAIN_LEN;
    canvas.height = GRAIN_CROSS;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('grain plate');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source as CanvasImageSource, 0, 0, GRAIN_LEN, GRAIN_CROSS);
    return { canvas, mean: plateMean(canvas) };
  });
  return plates;
}
